import React, { useState, useEffect, useCallback } from 'react';
import { Image } from '../data/apartmentData';

interface LightboxProps {
  images: Image[];
  initialIndex: number;
  onClose: () => void;
  apartmentId: string;
}

export const Lightbox: React.FC<LightboxProps> = ({
  images,
  initialIndex,
  onClose,
  apartmentId
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isLoading, setIsLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  const currentImage = images[currentIndex];

  // Reset loading state when current image changes
  useEffect(() => {
    setIsLoading(true);
    setImageError(false);
  }, [currentIndex, currentImage.url]);

  // Navigation functions
  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  const goToIndex = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowLeft':
          goToPrevious();
          break;
        case 'ArrowRight':
          goToNext();
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, goToNext, goToPrevious]);

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleImageLoad = () => {
    setIsLoading(false);
    setImageError(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setImageError(true);
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="lightbox-overlay" onClick={handleBackdropClick}>
      <div className="lightbox-container">
        {/* Header */}
        <div className="lightbox-header">
          <div className="lightbox-title">
            {apartmentId} - {currentImage.alt}
          </div>
          <div className="lightbox-counter">
            {currentIndex + 1} / {images.length}
          </div>
          <button 
            className="lightbox-close"
            onClick={onClose}
            aria-label="Close gallery"
          >
            ×
          </button>
        </div>

        {/* Main image area */}
        <div className="lightbox-main">
          {/* Navigation arrows */}
          {images.length > 1 && (
            <>
              <button 
                className="lightbox-nav lightbox-nav-prev"
                onClick={goToPrevious}
                aria-label="Previous image"
              >
                ‹
              </button>
              <button 
                className="lightbox-nav lightbox-nav-next"
                onClick={goToNext}
                aria-label="Next image"
              >
                ›
              </button>
            </>
          )}

          {/* Loading indicator */}
          {isLoading && !imageError && (
            <div className="lightbox-loading">
              <div className="lightbox-spinner"></div>
            </div>
          )}

          {/* Error indicator */}
          {imageError && (
            <div className="lightbox-error">
              <div className="lightbox-error-message">
                <div style={{
                  width: '400px',
                  height: '300px',
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300' viewBox='0 0 400 300'%3E%3Cdefs%3E%3Cpattern id='grid' width='20' height='20' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 20 0 L 0 0 0 20' fill='none' stroke='%23e0e8f0' stroke-width='0.5'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='400' height='300' fill='%23f8fafc' stroke='%23cbd5e1' stroke-width='2'/%3E%3Crect width='400' height='300' fill='url(%23grid)'/%3E%3Cg stroke='%23374151' stroke-width='2' fill='none'%3E%3Crect x='50' y='50' width='300' height='200'/%3E%3Cline x1='50' y1='150' x2='200' y2='150'/%3E%3Cline x1='200' y1='50' x2='200' y2='250'/%3E%3Cline x1='200' y1='200' x2='350' y2='200'/%3E%3Cline x1='280' y1='200' x2='280' y2='250'/%3E%3Crect x='60' y='60' width='20' height='8' fill='%23374151'/%3E%3Crect x='310' y='210' width='30' height='15' fill='none' stroke='%23374151' stroke-width='1'/%3E%3Ccircle cx='320' cy='220' r='3' fill='%23374151'/%3E%3Crect x='210' y='60' width='40' height='20' fill='none' stroke='%23374151' stroke-width='1'/%3E%3Cpath d='M220 80 Q230 85 240 80' stroke='%23374151' stroke-width='1' fill='none'/%3E%3C/g%3E%3Ctext x='125' y='110' text-anchor='middle' font-family='Arial, sans-serif' font-size='10' fill='%23374151'%3EWohnzimmer%3C/text%3E%3Ctext x='125' y='190' text-anchor='middle' font-family='Arial, sans-serif' font-size='10' fill='%23374151'%3EK%C3%BCche%3C/text%3E%3Ctext x='275' y='130' text-anchor='middle' font-family='Arial, sans-serif' font-size='10' fill='%23374151'%3ESchlafzimmer%3C/text%3E%3Ctext x='315' y='235' text-anchor='middle' font-family='Arial, sans-serif' font-size='10' fill='%23374151'%3EBad%3C/text%3E%3Ctext x='200' y='30' text-anchor='middle' font-family='Arial, sans-serif' font-size='12' font-weight='bold' fill='%23374151'%3EGrundriss%3C/text%3E%3C/svg%3E")`,
                  backgroundSize: 'contain',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center'
                }}>
                </div>
              </div>
            </div>
          )}

          {/* Main image */}
          <img
            key={`${currentIndex}-${currentImage.url}`}
            src={currentImage.url}
            alt={currentImage.alt}
            className="lightbox-image"
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{ opacity: isLoading || imageError ? 0 : 1 }}
          />
        </div>

        {/* Thumbnail navigation */}
        {images.length > 1 && (
          <div className="lightbox-thumbnails">
            {images.map((image, index) => (
              <button
                key={index}
                className={`lightbox-thumbnail ${index === currentIndex ? 'active' : ''}`}
                onClick={() => goToIndex(index)}
                aria-label={`Go to image ${index + 1}`}
              >
                <img
                  src={image.url}
                  alt={image.alt}
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}; 